import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Avatar } from "@/components/Avatar";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Ban, LogOut, RefreshCw, Shield, KeyRound } from "lucide-react";

export default function AdminDownLow() {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [activeRoomId, setActiveRoomId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadRooms = useCallback(async () => {
    const { data: pub } = await api.get("/rooms/public");
    // Admin page focuses on code-gated rooms (DownLow + future ones)
    setRooms(pub.filter((r) => r.requires_code));
    if (!activeRoomId && pub.length) {
      const dl = pub.find((r) => r.requires_code) || pub[0];
      if (dl) setActiveRoomId(dl.id);
    }
  }, [activeRoomId]);

  const loadAccess = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(`/admin/rooms/${id}/access`);
      setData(d);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRooms(); }, [loadRooms]);
  useEffect(() => { if (activeRoomId) loadAccess(activeRoomId); }, [activeRoomId, loadAccess]);

  // Refresh online status every 5s while on page
  useEffect(() => {
    if (!activeRoomId) return;
    const t = setInterval(() => loadAccess(activeRoomId), 5000);
    return () => clearInterval(t);
  }, [activeRoomId, loadAccess]);

  const kick = async (uid, username) => {
    try {
      await api.post(`/admin/rooms/${activeRoomId}/kick/${uid}`);
      toast.success(`Kicked ${username}`);
      loadAccess(activeRoomId);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const ban = async (uid, username) => {
    if (!window.confirm(`Ban @${username}? They'll need to be unbanned to return.`)) return;
    try {
      await api.post(`/admin/rooms/${activeRoomId}/ban/${uid}`);
      toast.success(`Banned ${username}`);
      loadAccess(activeRoomId);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const unban = async (uid, username) => {
    try {
      await api.post(`/admin/rooms/${activeRoomId}/unban/${uid}`);
      toast.success(`Unbanned ${username}`);
      loadAccess(activeRoomId);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  if (user?.role !== "admin") {
    return (
      <div className="min-h-screen grain">
        <Header />
        <div className="max-w-3xl mx-auto p-10 text-center">
          <Shield className="w-10 h-10 text-rose-400 mx-auto mb-3" />
          <h1 className="font-display text-2xl font-bold">Admin only</h1>
          <p className="text-zinc-500 mt-2">Sign in with the admin account to access this page.</p>
          <Link to="/lobby" className="ghost-btn inline-flex items-center gap-2 mt-6"><ArrowLeft className="w-4 h-4" /> Back to lobby</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grain">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/lobby" className="text-xs uppercase tracking-widest text-zinc-500 hover:text-white inline-flex items-center gap-1 mb-6">
          <ArrowLeft className="w-3 h-3" /> Back
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-5 h-5 text-fuchsia-400" />
          <div className="text-[10px] tracking-[0.3em] uppercase text-fuchsia-400 font-semibold">Admin · Code-gated rooms</div>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Who's behind the curtain</h1>
        <p className="text-zinc-400 mt-3 max-w-2xl">Monitor who has accepted into DownLow. Kick to revoke access (they can re-enter with the code). Ban to block them permanently.</p>

        {/* Room tabs if more than one code-gated room */}
        {rooms.length > 1 && (
          <div className="mt-8 flex flex-wrap gap-2">
            {rooms.map((r) => (
              <button
                key={r.id}
                onClick={() => setActiveRoomId(r.id)}
                className={`px-4 py-2 rounded-full text-sm transition border ${
                  activeRoomId === r.id
                    ? "bg-[#e81cff] border-[#e81cff] text-white"
                    : "border-white/10 hover:bg-white/5 text-zinc-300"
                }`}
              >
                {r.name}
              </button>
            ))}
          </div>
        )}

        {data && (
          <>
            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              <Stat label="Online now" value={data.online_count} accent="emerald" />
              <Stat label="Members with access" value={data.allowed.length} />
              <Stat label="Banned" value={data.banned.length} accent="rose" />
            </div>

            <div className="mt-6 rounded-2xl border border-fuchsia-500/20 bg-fuchsia-500/5 p-4 flex items-center justify-between flex-wrap gap-3">
              <div className="inline-flex items-center gap-2 text-sm">
                <KeyRound className="w-4 h-4 text-fuchsia-300" />
                <span className="text-zinc-400">Current {data.name} code:</span>
                <span data-testid="admin-access-code" className="font-display font-bold tracking-[0.2em] text-white">{data.access_code}</span>
              </div>
              <button onClick={() => loadAccess(activeRoomId)} className="ghost-btn inline-flex items-center gap-2 px-4 py-2" data-testid="admin-refresh-btn">
                <RefreshCw className="w-4 h-4" /> Refresh
              </button>
            </div>

            <section className="mt-8">
              <h2 className="font-display text-xl font-bold tracking-tight mb-3">Members with access ({data.allowed.length})</h2>
              {loading && <div className="text-zinc-500 text-sm">Loading…</div>}
              <div className="grid sm:grid-cols-2 gap-3">
                {data.allowed.map((u) => (
                  <UserRow key={u.id} u={u} onKick={kick} onBan={ban} />
                ))}
                {data.allowed.length === 0 && <div className="text-sm text-zinc-500">No one has entered yet.</div>}
              </div>
            </section>

            {data.banned.length > 0 && (
              <section className="mt-10">
                <h2 className="font-display text-xl font-bold tracking-tight mb-3 text-rose-300">Banned ({data.banned.length})</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {data.banned.map((u) => (
                    <BannedRow key={u.id} u={u} onUnban={unban} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, accent }) {
  const color =
    accent === "emerald" ? "text-emerald-400"
    : accent === "rose" ? "text-rose-400"
    : "text-fuchsia-400";
  return (
    <div className="rounded-2xl border border-white/5 bg-[#0c0c0f] p-5">
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">{label}</div>
      <div className={`font-display text-3xl font-black mt-2 ${color}`}>{value}</div>
    </div>
  );
}

function UserRow({ u, onKick, onBan }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-white/5 bg-[#0c0c0f] p-3" data-testid={`admin-user-row-${u.id}`}>
      <Avatar user={u} size={40} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold truncate">{u.username}</span>
          {u.is_online && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-400"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> online</span>}
          {u.role === "admin" && <span className="text-[10px] uppercase tracking-widest text-fuchsia-300">admin</span>}
        </div>
        <div className="text-xs text-zinc-500 truncate">{u.email || (u.is_guest ? "guest" : "—")}</div>
      </div>
      <button onClick={() => onKick(u.id, u.username)} data-testid={`kick-${u.id}`} className="text-xs px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5 inline-flex items-center gap-1" title="Remove access — can rejoin with code">
        <LogOut className="w-3 h-3" /> Kick
      </button>
      <button onClick={() => onBan(u.id, u.username)} data-testid={`ban-${u.id}`} className="text-xs px-3 py-1.5 rounded-full bg-rose-500/10 border border-rose-500/30 text-rose-300 hover:bg-rose-500/20 inline-flex items-center gap-1" title="Permanently block">
        <Ban className="w-3 h-3" /> Ban
      </button>
    </div>
  );
}

function BannedRow({ u, onUnban }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-rose-500/20 bg-rose-500/5 p-3 opacity-80">
      <Avatar user={u} size={40} />
      <div className="flex-1 min-w-0">
        <div className="font-semibold truncate">{u.username}</div>
        <div className="text-xs text-zinc-500 truncate">{u.email || "guest"}</div>
      </div>
      <button onClick={() => onUnban(u.id, u.username)} data-testid={`unban-${u.id}`} className="text-xs px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5">Unban</button>
    </div>
  );
}
